import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import type {SavedFilter} from '../hooks/useSavedFilters';
import type {DrawerFilterState} from '../hooks/useFilterDrawer';

interface Props {
	filters: SavedFilter[];
	onLoad: (criteria: Partial<DrawerFilterState>) => void;
	onSave: (name: string) => void;
	onDelete: (id: string) => void;
}

const Section = styled.div`
	border-bottom: 1px solid #f1f5f9;
	padding-bottom: 12px;
`;

const Head = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
`;

const SectionLabel = styled.span`
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #334155;
`;

const InputRow = styled.div`
	display: flex;
	gap: 8px;
	margin-bottom: 8px;
`;

const NameInput = styled.input`
	flex: 1 1 0%;
	border-radius: 6px;
	border: 1px solid #e2e8f0;
	padding: 6px 10px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #334155;
	&::placeholder {
		color: #94a3b8;
	}
	&:focus {
		outline: none;
		border-color: #a5b4fc;
		box-shadow: 0 0 0 1px #a5b4fc;
	}
`;

const EmptyText = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
`;

const Chips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
`;

const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border-radius: 9999px;
	border: 1px solid #e2e8f0;
	background: #f8fafc;
	padding: 4px 10px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: #475569;
`;

export function FilterDrawerSavedFilters({filters, onLoad, onSave, onDelete}: Props) {
	const [showInput, setShowInput] = useState(false);
	const [name, setName] = useState('');

	function handleSave() {
		const trimmed = name.trim();
		if (!trimmed) return;
		onSave(trimmed);
		setName('');
		setShowInput(false);
	}

	return (
		<Section>
			<Head>
				<SectionLabel>Saved Filters</SectionLabel>
				<Button emphasis={EMPHASIS.MEDIUM} onPress={() => setShowInput(v => !v)}>
					{showInput ? 'Cancel' : '+ Save current'}
				</Button>
			</Head>

			{showInput && (
				<InputRow>
					<NameInput
						type="text"
						placeholder="Filter name..."
						value={name}
						onChange={e => setName(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && handleSave()}
					/>
					<Button emphasis={EMPHASIS.HIGH} onPress={handleSave} disabled={!name.trim()}>
						Save
					</Button>
				</InputRow>
			)}

			{filters.length === 0 ? (
				<EmptyText>No saved filters yet</EmptyText>
			) : (
				<Chips>
					{filters.map(f => (
						<Chip key={f.id}>
							<Button emphasis={EMPHASIS.LOW} onPress={() => onLoad(f.criteria as Partial<DrawerFilterState>)}>
								{f.name}
							</Button>
							<LabelledButton emphasis={EMPHASIS.LOW} onPress={() => onDelete(f.id)} label={`Delete ${f.name}`}>
								&times;
							</LabelledButton>
						</Chip>
					))}
				</Chips>
			)}
		</Section>
	);
}
